import React, { useEffect, useState } from 'react';
import { formatPhoneInput } from '@/lib/utils';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Calendar, MapPin, Video, Users, ShieldAlert, ShieldCheck, CheckCircle2, Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { publicAPI } from '@/lib/publicApi';
import { formatEventDateTime } from '@/lib/eventTime';

/**
 * Phase 27.30.16 — Public tokenized event registration page.
 *
 * Anonymous. No login. The form adapts based on `event.requires_npn`:
 *  - true  → NPN + full contact (agent-only training/certification).
 *  - false → first + last name only (public celebration/party).
 *
 * When an unrecognised NPN is submitted, the backend transparently
 * creates a Prospect entry for recruiting (referred by the agent who
 * shared this link).
 */
export default function PublicShareEventRegisterPage() {
  const { token } = useParams();
  const [event, setEvent] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | not_found | revoked | error
  const [form, setForm] = useState({
    first_name: '', last_name: '',
    email: '', phone: '', npn: '',
    company: '', notes: '',
    consent_recruit: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { outcome, message, prospect_created }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setStatus('loading');
      try {
        const r = await publicAPI.getShareEvent(token);
        if (cancelled) return;
        setEvent(r.data);
        setStatus('ready');
      } catch (e) {
        if (cancelled) return;
        const code = e.response?.status;
        setStatus(code === 410 ? 'revoked' : code === 404 ? 'not_found' : 'error');
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: typeof v === 'string' ? v : v?.target?.value ?? v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim()) {
      toast.error('Please enter your first and last name.');
      return;
    }
    if (event.requires_npn) {
      if (!form.npn.trim()) {
        toast.error('This event requires an NPN.');
        return;
      }
      if (!form.email.trim() || !form.phone.trim()) {
        toast.error('Email and phone are required for NPN registrations.');
        return;
      }
    }
    setSubmitting(true);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
      };
      if (form.email.trim()) payload.email = form.email.trim();
      if (form.phone.trim()) payload.phone = form.phone.trim();
      if (form.npn.trim()) payload.npn = form.npn.trim();
      if (form.company.trim()) payload.company = form.company.trim();
      if (form.notes.trim()) payload.notes = form.notes.trim();
      if (form.consent_recruit) payload.consent_recruit = true;
      const r = await publicAPI.registerViaShareLink(token, payload);
      setResult(r.data);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (status === 'loading') {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" />
        <p className="text-sm text-slate-500 mt-2">Loading event…</p>
      </div>
    );
  }

  if (status === 'not_found') {
    return (
      <EmptyState
        title="Link not found"
        desc="This share link doesn't match any active event."
      />
    );
  }
  if (status === 'revoked') {
    return (
      <EmptyState
        title="Link revoked"
        desc="The organizer revoked this share link. Please contact the person who shared it with you."
      />
    );
  }
  if (status === 'error') {
    return (
      <EmptyState
        title="Something went wrong"
        desc="Please try again in a moment."
      />
    );
  }

  if (result) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12" data-testid="share-register-success">
        <Card>
          <CardContent className="p-8 text-center">
            <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
            <h1 className="text-2xl font-semibold text-slate-900 mb-1">You're in!</h1>
            <p className="text-sm text-slate-600 mb-4">{result.message}</p>
            {result.prospect_created && (
              <p className="text-xs text-slate-500 max-w-md mx-auto">
                We noticed you provided an NPN that's not in our organization
                — a member of the KR Enterprise Group team will reach out
                shortly to talk about becoming part of the team.
              </p>
            )}
            <p className="text-[11px] text-slate-400 mt-6">
              Confirmation ID: <span className="font-mono">{result.confirmation_id}</span>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const cover = event.cover_image_url
    ? (event.cover_image_url.startsWith('http')
        ? event.cover_image_url
        : `${process.env.REACT_APP_BACKEND_URL}${event.cover_image_url}`)
    : null;

  return (
    <div className="max-w-2xl mx-auto px-4 py-8" data-testid="share-register-page">
      <Card className="overflow-hidden">
        {cover && (
          <div className="aspect-[16/6] bg-slate-100 overflow-hidden">
            <img src={cover} alt="" className="w-full h-full object-cover" />
          </div>
        )}
        <CardContent className="p-6 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <h1 className="text-2xl font-semibold text-slate-900" data-testid="share-event-title">
              {event.title}
            </h1>
            <Badge
              variant="outline"
              className={event.requires_npn
                ? 'text-amber-800 bg-amber-50 border-amber-200 shrink-0'
                : 'text-emerald-800 bg-emerald-50 border-emerald-200 shrink-0'}
            >
              {event.requires_npn
                ? (<><ShieldAlert className="h-3 w-3 mr-1" /> Agents only</>)
                : (<><ShieldCheck className="h-3 w-3 mr-1" /> Open to all</>)}
            </Badge>
          </div>
          <div className="text-xs text-slate-600 space-y-1">
            <div className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" />
              {formatEventDateTime(event.start_datetime, event.end_datetime, event.timezone)}
            </div>
            {event.physical_location?.city && (
              <div className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />
                {[event.physical_location.street, event.physical_location.city, event.physical_location.state].filter(Boolean).join(', ')}
              </div>
            )}
            {event.virtual_meeting_url && (
              <div className="flex items-center gap-1.5"><Video className="h-3.5 w-3.5" /> Virtual event</div>
            )}
            {event.shared_by_name && (
              <div className="flex items-center gap-1.5 text-slate-500 pt-1">
                <Users className="h-3.5 w-3.5" /> Shared by <strong>{event.shared_by_name}</strong>
              </div>
            )}
          </div>
          {event.description && (
            <p className="text-sm text-slate-700 whitespace-pre-wrap pt-2 border-t border-slate-100">
              {event.description}
            </p>
          )}
        </CardContent>
      </Card>

      <Card className="mt-4">
        <CardContent className="p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-1">Register</h2>
          <p className="text-xs text-slate-500 mb-4">
            {event.requires_npn
              ? 'This is an agent-only event. Please provide your NPN and contact info.'
              : 'Enter your name to reserve a spot. Guests welcome!'}
          </p>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="First name *">
                <Input value={form.first_name} onChange={set('first_name')}
                  required maxLength={100} data-testid="share-field-first" />
              </Field>
              <Field label="Last name *">
                <Input value={form.last_name} onChange={set('last_name')}
                  required maxLength={100} data-testid="share-field-last" />
              </Field>
            </div>
            {event.requires_npn && (
              <>
                <Field label="NPN *">
                  <Input value={form.npn} onChange={set('npn')}
                    required maxLength={40} data-testid="share-field-npn"
                    placeholder="Your National Producer Number" />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Email *">
                    <Input type="email" value={form.email} onChange={set('email')}
                      required data-testid="share-field-email" />
                  </Field>
                  <Field label="Phone *">
                    <Input value={formatPhoneInput(form.phone)} onChange={set('phone')}
                      required maxLength={40} data-testid="share-field-phone" />
                  </Field>
                </div>
                <Field label="Current agency (optional)">
                  <Input value={form.company} onChange={set('company')}
                    maxLength={200} data-testid="share-field-company" />
                </Field>
                <Field label="Anything you'd like the organizer to know? (optional)">
                  <Textarea value={form.notes} onChange={set('notes')}
                    rows={3} maxLength={1000} data-testid="share-field-notes" />
                </Field>
                <label className="flex items-start gap-2 cursor-pointer text-xs text-slate-600">
                  <Checkbox
                    checked={form.consent_recruit}
                    onCheckedChange={(v) => setForm((f) => ({ ...f, consent_recruit: !!v }))}
                    className="mt-0.5"
                    data-testid="share-field-consent"
                  />
                  <span>
                    I consent to being contacted by KR Enterprise Group if
                    my NPN is not currently in their organization, so they
                    can share partnership opportunities.
                  </span>
                </label>
              </>
            )}
            {!event.requires_npn && (
              <div className="grid grid-cols-2 gap-3">
                <Field label="Email (optional)">
                  <Input type="email" value={form.email} onChange={set('email')}
                    data-testid="share-field-email" />
                </Field>
                <Field label="Phone (optional)">
                  <Input value={formatPhoneInput(form.phone)} onChange={set('phone')}
                    maxLength={40} data-testid="share-field-phone" />
                </Field>
              </div>
            )}
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#0f2a5e] hover:bg-[#0a1f47]"
              data-testid="share-submit"
            >
              {submitting ? 'Submitting…' : 'Register'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <p className="text-[11px] text-slate-400 text-center mt-4">
        Powered by KR Enterprise Group. <Link to="/" className="underline">Home</Link>
      </p>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs text-slate-600">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function EmptyState({ title, desc }) {
  return (
    <div className="max-w-lg mx-auto px-4 py-16 text-center">
      <h1 className="text-xl font-semibold text-slate-900 mb-1">{title}</h1>
      <p className="text-sm text-slate-600">{desc}</p>
      <Link to="/" className="text-xs text-slate-500 underline mt-6 inline-block">
        Back to KR Enterprise Group
      </Link>
    </div>
  );
}
